import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionApi, SubscriptionDetail } from '../../services/subscriptionApi';

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (dateStr: string | null) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ManageBilling: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, refreshUser } = useAuth();
  const [subscription, setSubscription] = useState<SubscriptionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showActivatedAcknowledgement, setShowActivatedAcknowledgement] = useState(false);
  const hasLoadedRef = useRef(false);
  const [cancelStep, setCancelStep] = useState<'idle' | 'confirm_trial' | 'choose_paid' | 'confirm_immediate' | 'confirm_end_cycle'>('idle');
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      const state = location.state as { subscriptionActivated?: boolean } | null;
      if (state?.subscriptionActivated) {
        setShowActivatedAcknowledgement(true);
        navigate(location.pathname, { replace: true, state: {} });
      }
      subscriptionApi
        .getMySubscription()
        .then((res) => setSubscription(res.subscription ?? null))
        .catch(() => setSubscription(null))
        .finally(() => setLoading(false));
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCancelTrial = async () => {
    setCancelError('');
    setIsCancelling(true);
    try {
      await subscriptionApi.cancelSubscription(true);
      await refreshUser();
      const res = await subscriptionApi.getMySubscription();
      setSubscription(res.subscription ?? null);
      setShowActivatedAcknowledgement(false);
      setCancelStep('idle');
    } catch (e: any) {
      setCancelError(e?.message || 'Failed to cancel. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  const handleCancelPaid = async (cancelAtPeriodEnd: boolean) => {
    setCancelError('');
    setIsCancelling(true);
    try {
      await subscriptionApi.cancelSubscription(cancelAtPeriodEnd);
      await refreshUser();
      const res = await subscriptionApi.getMySubscription();
      setSubscription(res.subscription ?? null);
      setShowActivatedAcknowledgement(false);
      setCancelStep('idle');
    } catch (e: any) {
      setCancelError(e?.message || 'Failed to cancel. Please try again.');
    } finally {
      setIsCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-text-light-secondary dark:text-text-secondary">Loading...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-light dark:bg-background flex flex-col">
      <Header />

      <main className="flex-grow max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-surface-light dark:bg-background-card rounded-lg shadow-lg p-6 sm:p-8">
          <h1 className="text-3xl font-bold text-text-light-primary dark:text-text-primary mb-2">
            Manage Billing
          </h1>
          <p className="text-sm text-text-light-secondary dark:text-text-secondary mb-8">
            {user?.email}
          </p>

          {showActivatedAcknowledgement && !!subscription && !subscription.cancelAtPeriodEnd && (
            <div className="mb-8 bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 dark:border-green-600 p-4 rounded-r-lg">
              <p className="text-green-800 dark:text-green-200 font-semibold">
                Your Pro subscription has been activated.
              </p>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                You now have access to all Pro features. You can manage your subscription and billing below.
              </p>
            </div>
          )}

          {!subscription ? (
            <section className="mb-8">
              <div className="bg-gray-50 dark:bg-gray-800 border-l-4 border-gray-400 dark:border-gray-600 p-4">
                <p className="text-gray-800 dark:text-gray-200">
                  You don’t have an active Pro subscription.
                </p>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-2">
                  Upgrade to Pro to get more lists, more tickers per list, and all Pro indicators.
                </p>
                <button
                  onClick={() => navigate('/pricing')}
                  className="mt-4 bg-primary dark:bg-highlight-gold text-gray-900 px-4 py-2 rounded-md font-semibold hover:opacity-90 transition-opacity"
                >
                  View pricing
                </button>
              </div>
            </section>
          ) : (
            <>
              {/* 48-hour full refund banner */}
              {!showActivatedAcknowledgement && subscription.isEligibleFor48HourFullRefund && subscription.fullRefundWindowEndsAt && (
                <section className="mb-8 pb-8 border-b border-border-light dark:border-border-color">
                  <div className="bg-green-50 dark:bg-green-900/20 border-l-4 border-green-400 dark:border-green-600 p-4">
                    <p className="text-green-800 dark:text-green-200 font-semibold">
                      You’re eligible for a full refund
                    </p>
                    <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                      If you cancel and request a refund before{' '}
                      <strong>{formatDateTime(subscription.fullRefundWindowEndsAt)}</strong>, you may
                      request a full refund. After that, refunds are prorated based on days remaining.
                    </p>
                  </div>
                </section>
              )}

              {/* Subscription status */}
              <section className="mb-8 pb-8 border-b border-border-light dark:border-border-color">
                <h2 className="text-xl font-semibold text-text-light-primary dark:text-text-primary mb-4">
                  Subscription status
                </h2>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3">
                  <p className="text-text-light-primary dark:text-text-primary">
                    <span className="font-medium">Plan:</span> Pro (monthly)
                  </p>
                  {subscription.cancelAtPeriodEnd && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded p-3">
                      <p className="text-amber-800 dark:text-amber-200 text-sm font-medium">
                        Cancellation scheduled — you’ll keep Pro until{' '}
                        {formatDate(subscription.cancelAtPeriodEnd)}.
                      </p>
                    </div>
                  )}
                  {subscription.isOnTrial ? (
                    <>
                      <p>
                        <span className="font-medium text-text-light-primary dark:text-text-primary">Free trial start:</span>{' '}
                        <span className="text-text-light-secondary dark:text-text-secondary">
                          {formatDate(subscription.trialStartAt)}
                        </span>
                      </p>
                      <p>
                        <span className="font-medium text-text-light-primary dark:text-text-primary">Free trial end:</span>{' '}
                        <span className="text-text-light-secondary dark:text-text-secondary">
                          {formatDate(subscription.trialEndAt)}
                        </span>
                      </p>
                      <p>
                        <span className="font-medium text-text-light-primary dark:text-text-primary">Next billing date:</span>{' '}
                        <span className="text-text-light-secondary dark:text-text-secondary">
                          {formatDate(subscription.nextBillingDate)}
                        </span>
                        <span className="text-sm text-text-light-secondary dark:text-text-secondary ml-1">
                          (after trial)
                        </span>
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        <span className="font-medium text-text-light-primary dark:text-text-primary">Last payment:</span>{' '}
                        <span className="text-text-light-secondary dark:text-text-secondary">
                          {formatDate(subscription.lastPaidAt)}
                        </span>
                      </p>
                      <p>
                        <span className="font-medium text-text-light-primary dark:text-text-primary">Next billing date:</span>{' '}
                        <span className="text-text-light-secondary dark:text-text-secondary">
                          {formatDate(subscription.nextBillingDate)}
                        </span>
                      </p>
                    </>
                  )}
                </div>
              </section>

              {/* Cancel subscription */}
              {!subscription.cancelAtPeriodEnd && (
                <section className="mb-8">
                  <h2 className="text-xl font-semibold text-text-light-primary dark:text-text-primary mb-4">
                    {cancelStep === 'confirm_end_cycle' || cancelStep === 'confirm_immediate'
                      ? 'Are you sure?'
                      : 'Cancel subscription'}
                  </h2>

                  {subscription.isOnTrial ? (
                    <>
                      <p className="text-sm text-text-light-secondary dark:text-text-secondary mb-4">
                        If you cancel your free trial, you’ll keep Pro until the end of the trial period ({formatDate(subscription.trialEndAt)}). You won’t be charged.
                      </p>
                      {cancelStep !== 'confirm_trial' ? (
                        <button
                          onClick={() => setCancelStep('confirm_trial')}
                          className="w-full bg-red-600 dark:bg-red-700 text-white px-4 py-2 rounded-md font-semibold hover:bg-red-700 dark:hover:bg-red-800 transition-colors"
                        >
                          Cancel free trial
                        </button>
                      ) : (
                        <div className="space-y-3">
                          <p className="text-sm text-text-light-primary dark:text-text-primary">
                            Cancel your free trial? You’ll keep Pro until {formatDate(subscription.trialEndAt)}.
                          </p>
                          {cancelError && (
                            <p className="text-sm text-red-600 dark:text-red-400">{cancelError}</p>
                          )}
                          <div className="flex gap-3">
                            <button
                              onClick={handleCancelTrial}
                              disabled={isCancelling}
                              className="flex-1 bg-red-600 dark:bg-red-700 text-white px-4 py-2 rounded-md font-semibold hover:bg-red-700 dark:hover:bg-red-800 disabled:opacity-50"
                            >
                              {isCancelling ? 'Cancelling...' : 'Yes, cancel trial'}
                            </button>
                            <button
                              onClick={() => { setCancelStep('idle'); setCancelError(''); }}
                              disabled={isCancelling}
                              className="flex-1 bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-4 py-2 rounded-md font-semibold disabled:opacity-50"
                            >
                              Keep trial
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-text-light-secondary dark:text-text-secondary mb-4">
                        {cancelStep === 'confirm_end_cycle' || cancelStep === 'confirm_immediate'
                          ? "Please confirm that you'd like to cancel your paid subscription now."
                          : 'Choose how you’d like to cancel your paid subscription.'}
                      </p>
                      {cancelStep === 'idle' && (
                        <button
                          onClick={() => setCancelStep('choose_paid')}
                          className="w-full bg-red-600 dark:bg-red-700 text-white px-4 py-2 rounded-md font-semibold hover:bg-red-700 dark:hover:bg-red-800 transition-colors"
                        >
                          Cancel subscription
                        </button>
                      )}
                      {cancelStep === 'choose_paid' && (
                        <div className="space-y-4">
                          <div className="border border-border-light dark:border-border-color rounded-lg p-4 space-y-3">
                            <button
                              onClick={() => setCancelStep('confirm_end_cycle')}
                              className="w-full text-left px-4 py-3 rounded-md bg-rose-100 dark:bg-rose-900/40 border border-rose-300 dark:border-rose-700 text-rose-800 dark:text-rose-200 font-semibold hover:bg-rose-200 dark:hover:bg-rose-800/50 transition-colors"
                            >
                              End Pro at the end of my billing cycle ({formatDate(subscription.currentPeriodEnd)})
                            </button>
                            <p className="text-xs text-text-light-secondary dark:text-text-secondary px-4">
                              You’ll keep Pro until then. No refund.
                            </p>
                            <button
                              onClick={() => setCancelStep('confirm_immediate')}
                              className="w-full text-left px-4 py-3 rounded-md bg-rose-100 dark:bg-rose-900/40 border border-rose-300 dark:border-rose-700 text-rose-800 dark:text-rose-200 font-semibold hover:bg-rose-200 dark:hover:bg-rose-800/50 transition-colors"
                            >
                              End Pro immediately.
                            </button>
                            <p className="text-xs text-text-light-secondary dark:text-text-secondary px-4">
                              {subscription.isEligibleFor48HourFullRefund
                                ? 'You’re within the 48-hour window.  You will get a full refund if you end Pro immediately.'
                                : 'You will get a prorated refund based on the days remaining in your current billing cycle.'}
                            </p>
                          </div>
                          <button
                            onClick={() => setCancelStep('idle')}
                            className="w-full bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-4 py-2 rounded-md font-semibold"
                          >
                            Back
                          </button>
                        </div>
                      )}
                      {cancelStep === 'confirm_end_cycle' && (
                        <div className="space-y-3">
                          <p className="text-sm text-text-light-primary dark:text-text-primary">
                            Your subscription will end on {formatDate(subscription.currentPeriodEnd)}. You’ll keep Pro until then.
                          </p>
                          {cancelError && (
                            <p className="text-sm text-red-600 dark:text-red-400">{cancelError}</p>
                          )}
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleCancelPaid(true)}
                              disabled={isCancelling}
                              className="flex-1 bg-red-600 dark:bg-red-700 text-white px-4 py-2 rounded-md font-semibold disabled:opacity-50"
                            >
                              {isCancelling ? 'Cancelling...' : 'Confirm'}
                            </button>
                            <button
                              onClick={() => { setCancelStep('choose_paid'); setCancelError(''); }}
                              disabled={isCancelling}
                              className="flex-1 bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-4 py-2 rounded-md font-semibold disabled:opacity-50"
                            >
                              Back
                            </button>
                          </div>
                        </div>
                      )}
                      {cancelStep === 'confirm_immediate' && (
                        <div className="space-y-3">
                          <p className="text-sm text-text-light-primary dark:text-text-primary">
                            Pro will end immediately.  Depending on your payment method, it could take up to 1-2 weeks to see your refund.
                          </p>
                          {cancelError && (
                            <p className="text-sm text-red-600 dark:text-red-400">{cancelError}</p>
                          )}
                          <div className="flex gap-3">
                            <button
                              onClick={() => handleCancelPaid(false)}
                              disabled={isCancelling}
                              className="flex-1 bg-red-600 dark:bg-red-700 text-white px-4 py-2 rounded-md font-semibold disabled:opacity-50"
                            >
                              {isCancelling ? 'Cancelling...' : 'Confirm'}
                            </button>
                            <button
                              onClick={() => { setCancelStep('choose_paid'); setCancelError(''); }}
                              disabled={isCancelling}
                              className="flex-1 bg-gray-300 dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-4 py-2 rounded-md font-semibold disabled:opacity-50"
                            >
                              Back
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </section>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ManageBilling;
